import React, { useState } from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { Modal } from '../Modal/Modal';
import { carService } from '../../api/services/car.service';
import { APP_COLORS } from '@/config/colors';

export interface ChangeVehicleAlertProps {
  onChangeVehicle?: () => void;
  className?: string;
  orderId: string;
}

export const ChangeVehicleAlert: React.FC<ChangeVehicleAlertProps> = ({
  onChangeVehicle,
  className,
  orderId,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
    setError(null);
    setSuccess(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setUrl('');
    setComment('');
    setError(null);
    setSuccess(false);
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (error) setError(null);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const handleSubmit = async () => {
    if (!url.trim()) {
      setError('Proszę podać link do ogłoszenia');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      await carService.changeVehicle(orderId, {
        vehicleAdvertisementLink: url.trim(),
        customerComment: comment.trim() || undefined,
      });

      setSuccess(true);
      setIsSubmitting(false);

      // Close modal after 4 seconds, then refresh page data
      setTimeout(() => {
        closeModal();
        if (onChangeVehicle) {
          onChangeVehicle();
        }
      }, 4000);
    } catch (err) {
      setIsSubmitting(false);
      setError('Wystąpił błąd podczas zmiany samochodu. Spróbuj ponownie.');
      console.error('Błąd podczas zmiany auta:', err);
    }
  };

  return (
    <div className="mx-auto bg-white">
      <div
        className={clsx('container my-auto pb-[56px] md:pb-[42px]', className)}
      >
        <div
          className="flex w-full flex-1 flex-shrink flex-col flex-wrap items-start justify-center rounded-[15px] px-5 py-5 text-[13px] md:min-w-[240px] md:flex-row md:items-center md:px-[60px] md:py-[30px]"
          style={{ backgroundColor: APP_COLORS.gray[100] }}
        >
          <div className="flex-1 flex-shrink pb-4 text-sm font-normal leading-1 font-inter text-text-primary md:pb-0">
            Możesz zmienić samochód, który sprawdzi nasz ekspert. Wystarczy, że
            prześlesz nam nowy link do ogłoszenia.
          </div>
          <button
            onClick={openModal}
            className="inline-flex items-center gap-3.5 rounded-[50px] bg-primary px-[26px] py-[11px] text-center font-inter text-sm font-semibold leading-4 tracking-[0.2px] text-white"
          >
            <span>Zmień auto</span>
            <SVGIcon name="refresh" />
          </button>
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title="Wklej link do ogłoszenia sprzedaży auta"
        >
          {success ? (
            <div className="flex flex-col gap-4 items-center py-6">
              <div className="flex justify-center items-center w-16 h-16 bg-green-100 rounded-full">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-lg font-medium text-center text-text-primary">
                Zgłoszenie zostało wysłane!
              </p>
              <p className="text-sm text-center text-grayText">
                Nasz zespół zajmie się zmianą pojazdu w Twoim zamówieniu.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Podaj link do ogłoszenia pojazdu, który chcesz sprawdzić.
              </p>

              <div>
                <label
                  htmlFor="vehicle-url"
                  className="block mb-1 text-sm font-medium text-text-primary"
                >
                  Link do ogłoszenia *
                </label>
                <input
                  id="vehicle-url"
                  type="text"
                  value={url}
                  onChange={handleUrlChange}
                  placeholder="https://www.otomoto.pl/oferta/ogloszenie-ID6E2AAKt.html"
                  className={clsx(
                    'p-3 w-full text-sm rounded-md border',
                    error
                      ? 'border-red-600 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500',
                  )}
                />
              </div>

              <div>
                <label
                  htmlFor="customer-comment"
                  className="block mb-1 text-sm font-medium text-text-primary"
                >
                  Dodatkowe informacje (opcjonalnie)
                </label>
                <textarea
                  id="customer-comment"
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder="Np. numer telefonu do sprzedającego, preferowana data oględzin..."
                  rows={3}
                  className="p-3 w-full text-sm rounded-md border border-gray-300 resize-none focus:ring-blue-500"
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-3 justify-end mt-4">
                <button
                  onClick={closeModal}
                  className="rounded-[50px] border border-gray-300 px-6 py-3 text-sm font-semibold text-text-primary transition-all hover:bg-gray-50"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={clsx(
                    'px-6 py-3 text-sm font-semibold text-white transition-all rounded-[50px] bg-blue',
                    isSubmitting
                      ? 'opacity-70 cursor-not-allowed'
                      : 'hover:bg-primary-600',
                  )}
                >
                  {isSubmitting ? 'WYSYŁANIE...' : 'WYŚLIJ'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};
